import { Box, Tab, Tabs, Typography } from "@mui/material";
import { SquarePen } from "lucide-react";
import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Button from "../../common/components/Button";
import NotFoundContent from "../../common/components/NotFoundContent";
import { SPACING } from "../../common/constants/spacing";
import { useAsset } from "../api/asset";
import AssetActionsDropdown from "../components/AssetActionsDropdown";
import AssetAnalytics from "../components/AssetAnalytics";
import AssetMetadata from "../components/AssetMetadata";
import AssetThumbnail from "../components/AssetThumbnail";
import EditAssetModal from "../components/EditAssetModal/EditAssetModal";
import EpisodesTab from "../components/EpisodesTab";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`asset-tabpanel-${index}`}
      aria-labelledby={`asset-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const Asset = () => {
  const { t } = useTranslation("assets");
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const { asset, loading, fetchError, refetch, deleteAsset, deleting } =
    useAsset(id);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
  };

  const handleAssetUpdated = () => {
    refetch();
  };

  // Loading state
  if (loading && !asset) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner animation="border" />
      </Box>
    );
  }

  // 404 Error state - Asset not found
  if (fetchError && fetchError.response?.status === 404) {
    return (
      <NotFoundContent
        title={t("errors.notFound")}
        message={t("errors.notFoundMessage")}
        primaryButtonText={t("errors.viewAllAssets")}
        onPrimaryClick={() => (window.location.href = "/assets")}
        secondaryButtonText={t("errors.goBack")}
        onSecondaryClick={() => window.history.back()}
      />
    );
  }

  // General error state
  if (fetchError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          padding: 3,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: "500px" }}>
          <Typography variant="h4" sx={{ mb: 2, color: "error.main" }}>
            {t("errors.loadingError")}
          </Typography>
          <Typography variant="bodyMd" sx={{ mb: 3, color: "text.secondary" }}>
            {fetchError.message || t("errors.unexpectedError")}
          </Typography>
          <Button onClick={() => refetch()}>{t("errors.tryAgain")}</Button>
        </Box>
      </Box>
    );
  }

  // No asset loaded yet
  if (!asset) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner animation="border" />
      </Box>
    );
  }

  return (
    <Box>
      <AssetThumbnail asset={asset} />

      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 3,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h1"
            sx={{ mb: SPACING.sm }}
            data-testid="asset-title"
          >
            {asset.title}
          </Typography>

          <Typography
            variant="bodyMd"
            sx={{ mb: 0 }}
            data-testid="asset-description"
          >
            {asset.description &&
            asset.description !== "null" &&
            asset.description !== "None" &&
            asset.description.trim() !== ""
              ? asset.description
              : t("noDescription")}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{ flexShrink: 0, display: "flex", gap: 1 }}
          data-testid="asset-action-buttons"
        >
          <Button
            variant="secondary"
            onClick={handleEditClick}
            startIcon={<SquarePen size={18} />}
            data-testid="asset-edit-button"
          >
            {t("actions.edit")}
          </Button>
          <AssetActionsDropdown
            onEdit={handleEditClick}
            onDelete={deleteAsset}
            deleting={deleting}
          />
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="asset details tabs"
        >
          <Tab label={t("tabs.overview")} />
          {asset.asset_type === "series" && <Tab label={t("tabs.episodes")} />}
          <Tab label={t("tabs.analytics")} />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={activeTab} index={0}>
        <AssetMetadata asset={asset} onRefresh={refetch} />
      </TabPanel>

      {/* Episodes Tab (series only) */}
      {asset.asset_type === "series" && (
        <TabPanel value={activeTab} index={1}>
          <EpisodesTab seriesId={asset.id} />
        </TabPanel>
      )}

      {/* Analytics Tab */}
      <TabPanel value={activeTab} index={asset.asset_type === "series" ? 2 : 1}>
        <AssetAnalytics assetId={id} />
      </TabPanel>

      <EditAssetModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        assetId={id}
        onAssetUpdated={handleAssetUpdated}
      />
    </Box>
  );
};

export default Asset;
